import {inject, Injectable} from '@angular/core';
import {PortalMessageBroker} from '../portal/portal-message-broker';
import {Router} from '@angular/router';
import {from} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {DocumentsActions} from '../store/actions/documents.actions';
import {AppState} from '../interfaces/app-state.interface';
import {Store} from '@ngrx/store';
import {InteractiveAnalysesActions} from '../store/actions/interactive-analyses.actions';
import {InhaberEntitiesActions} from '@adnova/jf-ng-components';


export interface DocumentReadIdV1 {
  readonly inhaberId: string;
  readonly documentId: string;
  readonly ablageortBezeichnung: string;
}

export interface OpenAblageortV1 {
  readonly inhaberId: string;
  readonly path: string;
}

export interface OpenAnalyseV1 {
  readonly inhaberId: string;
  readonly analyseIdentifier: string;
}

export interface ReadAnalyseV1 {
  readonly inhaberId: string;
  readonly analyseIdentifier: string;
}

export interface UpdatedBetriebDataV1 {
  readonly betriebId: string;
}

@Injectable({
  providedIn: 'root'
})
export class IntentActionService {

  private _logger = inject(NGXLogger);
  private _portalMessageBroker = inject(PortalMessageBroker);
  private _store = inject(Store<AppState>);
  private _router = inject(Router);

  /**
   * Intent-Id für den Broadcast zur Information über ein gelesenes Dokument
   *
   * JSON Schema: src/assets/intents/de.just-farming.dokumentenpostfach_document.read_v1.json
   *
   * @private
   */
  private readonly _dopoDocumentReadIdV1 = 'de.just-farming:dokumentenpostfach:document.read';

  /**
   * Intent-Id für das Öffnen eines Ablageortes
   *
   * JSON Schema: src/assets/intents/de.just-farming.dokumentenpostfach_ablageort.open_v1.json
   *
   * @private
   */
  private readonly _dopoOpenAblageortV1 = 'de.just-farming:dokumentenpostfach:ablageort.open';

  /**
   * Intent-Id für das Öffnen einer Analyse
   *
   * JSON Schema: src/assets/intents/de.just-farming.dokumentenpostfach_analyse.open_v1.json
   *
   * @private
   */
  private readonly _dopoOpenAnalyseV1 = 'de.just-farming:dokumentenpostfach:analyse.open';

  /**
   * Intent-Id für das Lesen einer Analyse als Broadcast
   *
   * JSON Schema: src/assets/intents/de.just-farming.dokumentenpostfach_analyse.read_v1.json
   *
   * @private
   */
  private readonly _dopoReadAnalyseV1 = 'de.just-farming:dokumentenpostfach:analyse.read';

  /**
   * Intent-Id für den Broadcast zur Information über einen aktualisierten Betrieb
   *
   * @private
   */
  private readonly _betriebUpdatedIdV1 = 'de.just-farming:betriebsinformationen:betrieb.updated';

  constructor() {
    if (this.isRunningInPortal()) {

      // INFO: Dokument gelesen als gelesen markieren.
      this._portalMessageBroker.registerIntentCallback(this._dopoDocumentReadIdV1, '1', (data => {
        this.handleDocumentReadIdV1(data);
      }));

      // INFO: Ablageort öffen.
      this._portalMessageBroker.registerIntentCallback(this._dopoOpenAblageortV1, '1', (data => {
        this.handleOpenAblageortV1(data);
      }));

      // INFO: Interaktive Analyse öffnen.
      this._portalMessageBroker.registerIntentCallback(this._dopoOpenAnalyseV1, '1', (data => {
        this.handleOpenAnalyseV1(data);
      }));

      // INFO: Interaktive Analyse als gelesen markieren.
      this._portalMessageBroker.registerIntentCallback(this._dopoReadAnalyseV1, '1', (data => {
        this.handleReadAnalyseV1(data);
      }));

      // INFO: Betrieb aktualisiert.
      this._portalMessageBroker.registerIntentCallback(this._betriebUpdatedIdV1, '1', (data => {
        this.handleBetriebUpdated(data);
      }));

      this._portalMessageBroker.allIntentCallbackRegistered();
    }
  }

  /**
   * Prüft, ob die App im Portal läuft.
   */
  public isRunningInPortal(): boolean {
    return this._portalMessageBroker.isRunningInPortal();
  }

  /**
   * Wird ein Dokument angelegt, sollen die Tabellen und Badges darüber informiert werden.
   *
   * @param data Intent-Daten
   */
  public documentReadIdV1(data: DocumentReadIdV1): void {
    if (this.isRunningInPortal()) {
      this.doDocumentReadIdV1(data);
    } else {
      this.handleDocumentReadIdV1(data);
    }
  }

  private handleDocumentReadIdV1(
    data: DocumentReadIdV1
  ): void {
    this._store.dispatch(DocumentsActions.reloadUtilityWidget({
      reload: true,
    }));
  }

  private doDocumentReadIdV1(data: DocumentReadIdV1): void {
    this.doEmit(this._dopoDocumentReadIdV1, '1', data);
  }

  /**
   * Öffnet einen Ablageort
   *
   * @param data Intent-Daten
   */
  public openAblageortV1(data: OpenAblageortV1): void {
    if (this.isRunningInPortal()) {
      this.doOpenAblageortV1(data);
    } else {
      this.handleOpenAblageortV1(data);
    }
  }

  private handleOpenAblageortV1(
    data: OpenAblageortV1
  ): void {
    this._router.navigate(
      [`/overview/inhaber/${data.inhaberId}`],
      {
        queryParams: {
          show: 'folders',
          path: data.path,
        }
      }
    );
  }

  private doOpenAblageortV1(data: OpenAblageortV1): void {
    this.doEmit(this._dopoOpenAblageortV1, '1', data);
  }

  /**
   * Öffnet eine interaktive Analyse
   *
   * @param data Intent-Daten
   */
  public openAnalyseV1(data: OpenAnalyseV1): void {
    if (this.isRunningInPortal()) {
      this.doOpenAnalyseV1(data);
    } else {
      this.handleOpenAnalyseV1(data);
    }
  }

  private handleOpenAnalyseV1(
    data: OpenAnalyseV1
  ): void {

    // INFO: Hier muss vor dem Navigieren erst die Komponente abgeräumt und dann anschließend neu initialisiert werden,
    // da ansonsten nur die zuerst geladenen analyse angezeigt wird.
    this._router.navigate(
      [`/interactive-analysis/inhaber/${data.inhaberId}`],
      {
        queryParams: {
          'dashboard-identifier': data.analyseIdentifier,
        },
      }
    ).then(() => {
      this._store.dispatch(InteractiveAnalysesActions.clear());
    });
  }

  private doOpenAnalyseV1(data: OpenAnalyseV1): void {
    this.doEmit(this._dopoOpenAnalyseV1, '1', data);
  }

  /**
   * Informiert über das Lesen einer interaktiven Analyse
   *
   * @param data Intent-Daten
   */
  public readAnalyseV1(data: ReadAnalyseV1): void {
    if (this.isRunningInPortal()) {
      this.doReadAnalyseV1(data);
    } else {
      this.handleReadAnalyseV1(data);
    }
  }

  private handleReadAnalyseV1(
    data: ReadAnalyseV1
  ): void {
    this._store.dispatch(DocumentsActions.reloadUtilityWidget({
      reload: true,
    }));
  }

  /**
   * Löst das Aktualisieren der Daten des Betriebs aus.
   *
   * @param data
   */
  handleBetriebUpdated(
    data: UpdatedBetriebDataV1,
  ): void {
    this._logger.debug('betrieb updated intent received for betriebId:', data.betriebId);

    // INFO: Die Daten der Betriebe in der Betriebsauswahl müssen aktualisiert werden.
    this._store.dispatch(InhaberEntitiesActions.loadInhabers());
  }

  private doReadAnalyseV1(data: ReadAnalyseV1): void {
    this.doEmit(this._dopoReadAnalyseV1, '1', data);
  }

  private doEmit(
    intentId: string,
    intentVersion: string,
    data: any,
  ): void {
    if (!this.isRunningInPortal()) {
      throw new Error('app is not running in portal');
    }

    const promise = this._portalMessageBroker.emitIntent(intentId, intentVersion, data);
    from(promise)
      .subscribe(
        value => {
          this._logger.debug('IntentActionService.doEmit(): id=' + intentId + ' version=' + intentVersion + ' handles successful', value);
        },
        error => {
          this._logger.debug('IntentActionService.doEmit(): id=' + intentId + ' version=' + intentVersion + ' dispatch failed', error);
        },
      );
  }
}

